import { AxiosResponse } from 'axios';
import { api } from 'hooks/useApi';
import { Group, GroupConfigSource, GroupRawResponse } from 'types/misc';
import { Region } from 'utils/constants/location';
import type { CompanyLead, GroupRaw } from 'types';
import type { GROUP_TYPE } from 'utils/constants';

const getProjectGroup = (groupId: Group['id']) => {
  return `/group-configuration/${groupId}/`;
};

export type { GroupConfigSource };

export type GroupConfigMinimal = {
  id: number;
  name: string;
  type?: number;
  state_code: string;
  is_notification_enabled?: boolean;
  email_frequency?: string;
  source?: GroupConfigSource;
  adk_query_id?: string | null;
  version?: string | null;
  adk_session_id?: string | null;
};

const getGroupsForUser = (
  groupType?: GROUP_TYPE,
): Promise<AxiosResponse<GroupConfigMinimal[]>> => {
  const params = new URLSearchParams();
  if (groupType) {
    params.append('type', String(groupType));
  }

  const queryString = params.toString();

  return api.get(`saved-search/?${queryString}`);
};

type AddToCompaniesResponse = {
  added_to?: number[];
  already_in?: number[];
};

const postSaveCompanyToGroupConfigs = ({
  groupIds,
  companyId,
}: {
  groupIds: number[];
  companyId: number;
}): Promise<AxiosResponse<AddToCompaniesResponse>> => {
  return api.post(`group-configuration/companies/${companyId}`, {
    group_config_ids: groupIds,
  });
};

const getPresetFilters = ({
  presetFilterName,
  mercatorMetropolitanCode,
}: {
  presetFilterName: string;
  mercatorMetropolitanCode: Region;
}): Promise<AxiosResponse<{ data: GroupRawResponse; message: string }>> => {
  return api.post(`/preset-filters/${presetFilterName}/`, {
    mercator_metropolitan_code: mercatorMetropolitanCode,
  });
};

const getCompaniesForCompanyGroup = (
  groupId: number,
  page: number = 1,
): Promise<AxiosResponse<CompanyLead[]>> => {
  return api.get<CompanyLead[]>(`group-configuration/${groupId}/companies/?page=${page}`);
};

type CreateSavedSearchResponse = {
  data: {
    group_config_id?: number;
  };
  message: string;
};

const postCreateNewSavedSearch = (
  payload: GroupRaw,
  options?: { timeout?: number },
): Promise<AxiosResponse<CreateSavedSearchResponse>> => {
  return api.post<CreateSavedSearchResponse>('group-configuration/', payload, {
    timeout: options?.timeout ?? 30000,
  });
};

const deleteCompaniesFromGroupConfig = (groupConfigId: number, companyIds: number[]) => {
  return api.delete(`group-configuration-companies/${groupConfigId}/`, {
    data: { company_ids: companyIds },
  });
};

const postAddCompaniesToGroupConfig = (groupConfigId: number, companyIds: number[]) => {
  return api.post(`group-configuration-companies/${groupConfigId}/`, {
    company_ids: companyIds,
  });
};

const deleteGroupConfiguration = (groupId: string): Promise<AxiosResponse<any>> => {
  return api.delete(`/group-configuration/${groupId}/`);
};

const patchGroupConfiguration = (
  groupId: string,
  payload: GroupRaw,
  options?: { timeout?: number },
): Promise<AxiosResponse<any>> => {
  return api.patch(`/group-configuration/${groupId}/`, payload, {
    timeout: options?.timeout ?? 30000,
  });
};

const fetchGroupConfiguration = (
  groupId: string,
): Promise<AxiosResponse<{ data: GroupRawResponse; message: string }>> => {
  return api.get(getProjectGroup(groupId));
};

export default {
  getProjectGroup,
  getGroupsForUser,
  postSaveCompanyToGroupConfigs,
  getPresetFilters,
  getCompaniesForCompanyGroup,
  postCreateNewSavedSearch,
  deleteCompaniesFromGroupConfig,
  postAddCompaniesToGroupConfig,
  deleteGroupConfiguration,
  patchGroupConfiguration,
  fetchGroupConfiguration,
};
